/**
 * Idle-aware reload scheduler — Wave 5 #56.
 *
 * Triggers a reload only after the user has been idle for `idleThresholdMs`.
 * If a trigger fires while the user is mid-input, polls every `pollIntervalMs`
 * until the idle gap is large enough.
 *
 * Pure module — receives clock + activity getter + reload callback so tests
 * can drive deterministic timelines.
 */
export interface IdleReloadOpts {
  /** Required idle window before a reload may fire. */
  idleThresholdMs: number;
  /** How often to re-check while the user is still active. */
  pollIntervalMs: number;
  /** Returns the timestamp (ms) of the last user input event. */
  getLastActivityAt: () => number;
  /** Returns the current clock (ms). Defaults to Date.now in production. */
  now?: () => number;
  /** Schedules a callback. Defaults to setTimeout. */
  setTimer?: (cb: () => void, delayMs: number) => unknown;
  /** Performs the reload. Called at most once. */
  onReload: (reason: string) => void;
}

export interface IdleReloadScheduler {
  /** Queues a reload; first reason wins if multiple triggers fire. */
  schedule: (reason: string) => void;
  /** Whether a reload has already fired. */
  hasFired: () => boolean;
}

export function createIdleReloadScheduler(opts: IdleReloadOpts): IdleReloadScheduler {
  const now = opts.now ?? (() => Date.now());
  const setTimer = opts.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
  let fired = false;
  let pending = false;

  const tryReload = (reason: string) => {
    if (fired) return;
    const idleFor = now() - opts.getLastActivityAt();
    if (idleFor >= opts.idleThresholdMs) {
      fired = true;
      pending = false;
      opts.onReload(reason);
      return;
    }
    setTimer(() => tryReload(reason), opts.pollIntervalMs);
  };

  return {
    schedule(reason) {
      if (fired || pending) return;
      pending = true;
      tryReload(reason);
    },
    hasFired() {
      return fired;
    },
  };
}
